import React, { useEffect, useCallback, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import MainLayout from '../../../layouts/MainLayout'
import '../../../assets/styles/custom-styles.css'
import useNotification from '../../../hooks/useNotification'
import useLoader from '../../../hooks/useLoader'
import useForm from '../../../hooks/useForm'
import Form from '../../../components/forms/Form'
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
  transformObjectValues,
} from '../../../utils/objectUtils'
import { entities } from '../../../constants/entities'
import PageHeader from '../../../components/PageHeader'
import SimpleBody from '../../../components/forms/SimpleBody'
import SectionHeader from '../../../components/forms/SectionHeader'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import useBaseService from '../../../hooks/services/useBaseService'
import { editEquipmentFields } from '../../../constants/forms/equipmentFields'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { useSectionFields } from '../../../hooks/useSectionFields'

const EditEquipmentPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const { showLoader, hideLoader } = useLoader()
  const queryClient = useQueryClient()

  const { updateField: updateProps } = useSectionFields()

  const { filters, pagination } = useUrlFilters(
    {
      name: [],
      number: [],
      serial_number: [],
      status_id: [],
      deleted_at: false,
    },
    'equipments'
  )

  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate)

  const [editSection] = editEquipmentFields

  const { formData, handleChange, setFormData, resetForm } = useForm(
    setDefaultFieldValues([editSection]),
    [editSection]
  )

  const [fields, setFields] = useState(editSection.fields)

  const { data: equipmentData, isError: isEquipmentError } = useQueryWithLoader(
    {
      queryKey: ['equipment', id],
      queryFn: async () => {
        const response = await baseServiceGetByColumn(
          entities.equipments.getById(id)
        )
        return response.result
      },
      enabled: !!id,
      staleTime: Infinity,
    }
  )

  // tratar erros
  useEffect(() => {
    if (isEquipmentError) {
      showNotification('error', 'Erro ao buscar equipamento')
      navigate(`/aluguel/equipamentos`)
    }
  }, [isEquipmentError, navigate, showNotification])

  useEffect(() => {
    if (equipmentData) {
      setFormData({
        equipment: {
          name: equipmentData.name ?? '-',
          current_organization: equipmentData.organization.name ?? '-',
          number: equipmentData.number ?? '-',
          purchase_date:
            equipmentData.creation_transaction.purchase_date ?? '-',
          serial_number: equipmentData.serial_number ?? '-',
          warranty_date:
            equipmentData.creation_transaction_item.warranty_date ?? '-',
          condition: equipmentData.condition.name ?? '-',
          category: equipmentData.category.name ?? '-',
        },
      })
    }
  }, [equipmentData, setFormData])

  // * Bloqueiar a edição do tombo caso o equipamento tenha movimento
  useEffect(() => {
    if (!equipmentData) return // só roda quando os dados existem

    let updateFields = [...editSection.fields]

    if (equipmentData.last_relevant_movement_item) {
      const updated = updateProps({
        arrayDependente: updateFields,
        field_id: 'equipment.number',
        updates: { disabled: true },
      })
      console.log('updateProps result:', updated)
      updateFields = updated
    }

    setFields(updateFields)
  }, [editSection.fields, equipmentData, updateProps])

  const updateProductMutation = useMutation({
    mutationFn: async (updatedData) => {
      showLoader()
      setFormErrors({})
      const response = await baseServicePut(
        entities.equipments.update(id),
        updatedData
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader()
        resetForm()
        navigate(-1)
        queryClient.invalidateQueries(['equipment', filters, pagination])
      }
      hideLoader()
    },
    onError: (error) => {
      console.error('onError ', error)
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors)
      }
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const handleSubmit = async () => {
    try {
      const { equipment } = transformObjectValues(formData)
      const cleanedEquipment = cleanPayload(equipment)

      const payload = {
        equipment: {
          ...cleanedEquipment,
          serial_number: String(cleanedEquipment.serial_number),
        },
      }
      return await updateProductMutation.mutateAsync(payload)
    } catch (error) {
      console.error('Erro ao enviar produto:', {
        status: error.status,
        message: error.message,
      })
    }
  }

  const handleBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Editar Equipamento: ${formData?.equipment?.name || ''}`}
        showBackButton={true}
        backUrl="/aluguel/equipamentos/"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Salvar Alterações"
          textLoadingSubmit="Salvando..."
          handleBack={handleBack}
          isSubmitting={updateProductMutation.isPending}>
          {() => {
            return (
              <>
                <div key={editSection.section}>
                  <SectionHeader
                    section={editSection.section}
                    viewTable={false}
                    setViewTable={() => {}}
                    addFieldsInData={() => {}}
                  />
                  <SimpleBody
                    fields={fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={() => {}}
                    getSelectedValue={() => {}}
                  />
                </div>
              </>
            )
          }}
        </Form>
      </div>
    </MainLayout>
  )
}

export default EditEquipmentPage
